import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  CameraIcon,
  CheckCircleIcon,
  CheckIcon,
  Loader2Icon,
  PlusIcon,
  TrashIcon,
  UploadIcon,
  XIcon,
} from 'lucide-react'
import {
  addJobTask,
  addJobTasksBulk,
  deleteJobAttachment,
  deleteJobTask,
  fetchJobAttachments,
  fetchJobTasks,
  setJobTaskPhotoRequired,
  toggleJobTask,
  uploadJobFile,
  type JobAttachment,
  type JobCaptureSource,
  type JobTask,
} from '../../api/jobWorkflow'
import { useChecklistTemplates } from '../../hooks/useChecklistTemplates'
import {
  firstUnfinishedTaskId,
  nextUnfinishedTaskId,
  photoModeActionForTask,
  photoModeProgress,
} from '../../lib/checklistPhotoMode'
import {
  isChecklistImageFile,
  isPendingAttachmentId,
  mergeChecklistPhotos,
  mergeChecklistTasks,
} from '../../lib/checklistPhotoOptimistic'
import {
  countQueuedPhotosForJob,
  enqueueJobPhoto,
  listQueuedPhotosForJob,
} from '../../lib/offlinePhotoQueue'
import {
  isLikelyOfflineError,
  queuedPhotoToAttachment,
  syncOfflinePhotoQueue,
} from '../../lib/offlinePhotoSync'
import {
  cacheJobAttachments,
  cacheJobTasks,
  patchCachedJobTaskDone,
  readCachedJobAttachments,
  readCachedJobTasks,
} from '../../lib/offlineJobChecklistCache'
import { sortTemplatesForServiceType } from '../../lib/checklistTemplates'
import { workSpecsToChecklistItems } from '../../lib/jobWorkSpec'
import { supabase } from '../../lib/supabase'
import type { WorkSpecRow } from '../../types/agreement'
import { usePermissions } from '../../hooks/usePermissions'
import { canCompleteJobWithException } from '../../lib/jobCompletionException'
import { useTranslation } from 'react-i18next'
import {
  captureScroll,
  getScrollParent,
  restoreScroll,
  type ScrollSnapshot,
} from '../../utils/scrollParent'

const SCROLL_LOCK_PULSE_MS = [0, 50, 120, 280, 500, 900, 1400] as const

type SavedPhotoModeState = {
  photoMode: boolean
  taskId: string
  sessionTotal: number
}

function photoModeStorageKey(
  jobId: string,
  generalOnly: boolean,
  jobLineItemUnitId: string | null
) {
  return `rbc:photoMode:${jobId}|${generalOnly ? 1 : 0}|${jobLineItemUnitId || ''}`
}

function readSavedPhotoMode(key: string): SavedPhotoModeState | null {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SavedPhotoModeState
    if (!parsed?.photoMode || !parsed.taskId) return null
    return parsed
  } catch {
    return null
  }
}

function writeSavedPhotoMode(key: string, state: SavedPhotoModeState | null) {
  try {
    if (!state?.photoMode || !state.taskId) {
      sessionStorage.removeItem(key)
      return
    }
    sessionStorage.setItem(key, JSON.stringify(state))
  } catch {
    /* quota / private mode */
  }
}

interface JobTaskChecklistProps {
  jobId: string
  contractId?: string | null
  serviceType?: string | null
  /** When true, show "this visit only" badge on manual items for recurring jobs. */
  showOneOffBadges?: boolean
  /** Hide apartment-scoped tasks; those are rendered in ApartmentJobWork. */
  generalOnly?: boolean
  /** Show tasks for one apartment within a turnover job. */
  jobLineItemUnitId?: string | null
  /** Fired after tasks load or change (full job task list before apartment filter). */
  onTasksChange?: (tasks: JobTask[]) => void
  /** Enter Photo Mode after load when unfinished tasks remain. */
  startPhotoMode?: boolean
  /** Increment to re-enter Photo Mode without remounting the checklist. */
  startPhotoModeNonce?: number
  /** Field flow: guided photo mode only — hide full checklist editor UI. */
  flowOnly?: boolean
  /** flowOnly: complete job when checklist is done (My Day handler). */
  onCompleteJob?: () => void
  /** flowOnly: parent busy while completing the job. */
  completeBusy?: boolean
}

export function JobTaskChecklist({
  jobId,
  contractId,
  serviceType,
  showOneOffBadges = true,
  generalOnly = false,
  jobLineItemUnitId = null,
  onTasksChange,
  startPhotoMode = false,
  startPhotoModeNonce = 0,
  flowOnly = false,
  onCompleteJob,
  completeBusy = false,
}: JobTaskChecklistProps) {
  const { t: tJobs } = useTranslation('jobs')
  const { t: tField } = useTranslation('field')
  const { profile, can } = usePermissions()
  const canEditChecklist = can('jobs.edit')
  const canBypassPhoto = canCompleteJobWithException(profile?.role)
  const [tasks, setTasks] = useState<JobTask[]>([])
  const [attachments, setAttachments] = useState<JobAttachment[]>([])
  const [newLabel, setNewLabel] = useState('')
  const [newPhotoRequired, setNewPhotoRequired] = useState(false)
  const [loading, setLoading] = useState(true)
  const cameraRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const flowCameraRef = useRef<HTMLInputElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const taskRowRefs = useRef<Record<string, HTMLDivElement | null>>({})
  /** Saved before opening the camera/file picker — iOS PWA often resets main scroll on return. */
  const scrollSnapshotRef = useRef<ScrollSnapshot | null>(null)
  const scrollLockTaskIdRef = useRef<string | null>(null)
  const clearScrollLockTimerRef = useRef<number | null>(null)
  const scrollPulseTimersRef = useRef<number[]>([])
  const pendingBlobUrlsRef = useRef<string[]>([])
  const [photoMode, setPhotoMode] = useState(false)
  const [photoModeTaskId, setPhotoModeTaskId] = useState<string | null>(null)
  const [photoModeSessionTotal, setPhotoModeSessionTotal] = useState(0)
  const [queuedPhotoCount, setQueuedPhotoCount] = useState(0)
  const listKey = `${jobId}|${generalOnly ? 1 : 0}|${jobLineItemUnitId || ''}`
  const photoModeStorageKeyForJob = photoModeStorageKey(jobId, generalOnly, jobLineItemUnitId)
  const [activeListKey, setActiveListKey] = useState(listKey)
  const tasksRef = useRef(tasks)
  const photoModeRef = useRef(false)
  const autoStartKeyRef = useRef('')
  const photoModeInitKeyRef = useRef('')
  tasksRef.current = tasks
  photoModeRef.current = photoMode
  const { templates } = useChecklistTemplates()
  const sortedTemplates = sortTemplatesForServiceType(templates, serviceType)

  const revokePendingBlobs = (urls?: string[]) => {
    const list = urls || pendingBlobUrlsRef.current
    for (const url of list) {
      try {
        URL.revokeObjectURL(url)
      } catch {
        /* ignore */
      }
    }
    if (!urls) {
      pendingBlobUrlsRef.current = []
      return
    }
    const drop = new Set(urls)
    pendingBlobUrlsRef.current = pendingBlobUrlsRef.current.filter((u) => !drop.has(u))
  }

  const clearScrollPulses = () => {
    for (const id of scrollPulseTimersRef.current) window.clearTimeout(id)
    scrollPulseTimersRef.current = []
  }

  const clearScrollLock = () => {
    scrollSnapshotRef.current = null
    scrollLockTaskIdRef.current = null
    clearScrollPulses()
    if (clearScrollLockTimerRef.current != null) {
      window.clearTimeout(clearScrollLockTimerRef.current)
      clearScrollLockTimerRef.current = null
    }
  }

  const rememberScroll = (taskId?: string) => {
    if (clearScrollLockTimerRef.current != null) {
      window.clearTimeout(clearScrollLockTimerRef.current)
      clearScrollLockTimerRef.current = null
    }
    clearScrollPulses()
    const anchor = taskId ? taskRowRefs.current[taskId] : null
    scrollSnapshotRef.current = captureScroll(rootRef.current, anchor)
    if (taskId) scrollLockTaskIdRef.current = taskId
  }

  const applyRememberedScroll = () => {
    const snapshot = scrollSnapshotRef.current
    if (!snapshot) return
    const taskId = scrollLockTaskIdRef.current
    const row = taskId ? taskRowRefs.current[taskId] : null
    restoreScroll(snapshot, rootRef.current, row)
    // If shell scroll was lost (common after camera), bring the task back on screen.
    if (row && snapshot.mainTop > 0) {
      const main =
        getScrollParent(rootRef.current) ||
        (document.querySelector('main.shell-main-pad') as HTMLElement | null)
      const mainTop = main?.scrollTop ?? 0
      if (mainTop < snapshot.mainTop * 0.25) {
        row.scrollIntoView({ block: 'nearest', inline: 'nearest' })
        restoreScroll(snapshot, rootRef.current, row)
      }
    }
  }

  const pulseRememberedScroll = () => {
    clearScrollPulses()
    for (const ms of SCROLL_LOCK_PULSE_MS) {
      const id = window.setTimeout(() => {
        if (!scrollSnapshotRef.current) return
        applyRememberedScroll()
      }, ms)
      scrollPulseTimersRef.current.push(id)
    }
  }

  const scheduleClearScrollLock = (delayMs: number) => {
    if (clearScrollLockTimerRef.current != null) {
      window.clearTimeout(clearScrollLockTimerRef.current)
    }
    clearScrollLockTimerRef.current = window.setTimeout(() => {
      clearScrollLock()
    }, delayMs)
  }

  const filterTasks = (taskRows: JobTask[]) =>
    jobLineItemUnitId
      ? taskRows.filter((task) => task.job_line_item_unit_id === jobLineItemUnitId)
      : generalOnly
        ? taskRows.filter((task) => !task.job_line_item_unit_id)
        : taskRows

  const load = async (opts?: { forceDoneTaskIds?: string[] }) => {
    try {
      let taskRows: JobTask[]
      let atts: JobAttachment[]
      let fromNetwork = false
      try {
        ;[taskRows, atts] = await Promise.all([
          fetchJobTasks(jobId),
          fetchJobAttachments(jobId),
        ])
        fromNetwork = true
      } catch (e) {
        if (!isLikelyOfflineError(e)) throw e
        const cachedTasks = readCachedJobTasks(jobId)
        const cachedAtts = readCachedJobAttachments(jobId)
        if (!cachedTasks) throw e
        taskRows = cachedTasks
        atts = cachedAtts || []
      }

      const queued = await listQueuedPhotosForJob(jobId)
      const serverPhotos = atts.filter((a) => a.attachment_type === 'photo')
      const queuedPhotos = queued.map((item) => {
        const attachment = queuedPhotoToAttachment(item)
        pendingBlobUrlsRef.current.push(attachment.file_url)
        return attachment
      })
      const queuedAutoCheckTaskIds = queued
        .filter((item) => item.shouldAutoCheck && item.jobTaskId)
        .map((item) => item.jobTaskId as string)
      const forceDoneTaskIds = [
        ...(opts?.forceDoneTaskIds || []),
        ...queuedAutoCheckTaskIds,
      ]
      // Remount / PWA camera return: React prev is empty — keep optimistic
      // checkmarks from session cache while the server briefly lags.
      const cachedTasks = readCachedJobTasks(jobId) || []
      const mergedTaskRows = mergeChecklistTasks(taskRows, cachedTasks, {
        forceDoneTaskIds,
      })
      if (fromNetwork) {
        cacheJobTasks(jobId, mergedTaskRows)
        cacheJobAttachments(jobId, atts)
      }
      setAttachments((prev) => {
        const merged = mergeChecklistPhotos(serverPhotos, [...queuedPhotos, ...prev])
        const droppedPending = prev.filter(
          (a) => isPendingAttachmentId(a.id) && !merged.some((m) => m.id === a.id)
        )
        if (droppedPending.length) {
          revokePendingBlobs(droppedPending.map((a) => a.file_url))
        }
        return merged
      })
      setTasks((prev) =>
        mergeChecklistTasks(filterTasks(mergedTaskRows), prev, { forceDoneTaskIds })
      )
      onTasksChange?.(mergedTaskRows)
    } finally {
      setLoading(false)
    }
  }

  if (activeListKey !== listKey) {
    const [oldJobId, oldGeneral, oldUnit = ''] = activeListKey.split('|')
    writeSavedPhotoMode(photoModeStorageKey(oldJobId, oldGeneral === '1', oldUnit || null), null)
    setActiveListKey(listKey)
    setLoading(true)
    setPhotoMode(false)
    setPhotoModeTaskId(null)
    setPhotoModeSessionTotal(0)
    autoStartKeyRef.current = ''
    photoModeInitKeyRef.current = ''
  }

  useEffect(() => {
    setLoading(true)
    load()
  }, [jobId, generalOnly, jobLineItemUnitId])

  const enterPhotoMode = (taskList: JobTask[] = tasks) => {
    const first = firstUnfinishedTaskId(taskList)
    if (!first) {
      // flowOnly: still show "Checklist complete" + Complete job after remount
      // when cache/server already has every item done.
      if (flowOnly && taskList.length > 0) {
        setPhotoMode(true)
        setPhotoModeTaskId(null)
        setPhotoModeSessionTotal(taskList.length)
        writeSavedPhotoMode(photoModeStorageKeyForJob, null)
      }
      return
    }
    const sessionTotal = taskList.filter((t) => !t.is_done).length
    setPhotoMode(true)
    setPhotoModeTaskId(first)
    setPhotoModeSessionTotal(sessionTotal)
    writeSavedPhotoMode(photoModeStorageKeyForJob, {
      photoMode: true,
      taskId: first,
      sessionTotal,
    })
  }

  const exitPhotoMode = () => {
    setPhotoMode(false)
    setPhotoModeTaskId(null)
    writeSavedPhotoMode(photoModeStorageKeyForJob, null)
  }

  useEffect(() => {
    if (!photoMode || !photoModeTaskId) return
    writeSavedPhotoMode(photoModeStorageKeyForJob, {
      photoMode: true,
      taskId: photoModeTaskId,
      sessionTotal: photoModeSessionTotal,
    })
  }, [photoMode, photoModeTaskId, photoModeSessionTotal, photoModeStorageKeyForJob])

  useEffect(() => {
    if (loading) return

    const initKey = `${listKey}:${startPhotoModeNonce}`
    if (photoModeInitKeyRef.current === initKey) return
    photoModeInitKeyRef.current = initKey

    const saved = readSavedPhotoMode(photoModeStorageKeyForJob)
    if (saved?.photoMode) {
      const savedTask = tasks.find((t) => t.id === saved.taskId)
      if (savedTask && !savedTask.is_done) {
        setPhotoMode(true)
        setPhotoModeTaskId(saved.taskId)
        setPhotoModeSessionTotal(saved.sessionTotal || tasks.filter((t) => !t.is_done).length)
        const key = `${jobId}:${flowOnly ? 1 : 0}:${startPhotoMode ? 1 : 0}:${startPhotoModeNonce}`
        autoStartKeyRef.current = key
        return
      }
      writeSavedPhotoMode(photoModeStorageKeyForJob, null)
    }

    const shouldStart = flowOnly || startPhotoMode || startPhotoModeNonce > 0
    if (!shouldStart) return
    const key = `${jobId}:${flowOnly ? 1 : 0}:${startPhotoMode ? 1 : 0}:${startPhotoModeNonce}`
    if (autoStartKeyRef.current === key) return
    autoStartKeyRef.current = key
    enterPhotoMode(tasks)
  }, [jobId, flowOnly, startPhotoMode, startPhotoModeNonce, loading, tasks, photoModeStorageKeyForJob, listKey])

  // Re-apply saved scroll after React paints upload/refresh updates (and on camera return).
  useLayoutEffect(() => {
    if (!scrollSnapshotRef.current) return
    applyRememberedScroll()
  }, [tasks, attachments])

  useEffect(() => {
    const onReturn = () => {
      if (!scrollSnapshotRef.current) return
      // Double rAF + timed pulses: iOS often resets scroll after first paints following camera UI.
      requestAnimationFrame(() => {
        applyRememberedScroll()
        requestAnimationFrame(() => {
          applyRememberedScroll()
          pulseRememberedScroll()
          // Cancelled picker: don't keep fighting the user's scroll forever.
          scheduleClearScrollLock(1600)
        })
      })
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onReturn()
    }
    /** Re-assert scroll if the shell jumps toward top while a photo lock is active. */
    const onScroll = () => {
      const snapshot = scrollSnapshotRef.current
      if (!snapshot) return
      if (!scrollLockTaskIdRef.current) return
      const main =
        getScrollParent(rootRef.current) ||
        (document.querySelector('main.shell-main-pad') as HTMLElement | null)
      if (!main) return
      if (snapshot.mainTop > 40 && main.scrollTop < snapshot.mainTop * 0.5) {
        applyRememberedScroll()
      }
    }
    const main =
      getScrollParent(rootRef.current) ||
      (document.querySelector('main.shell-main-pad') as HTMLElement | null)
    window.addEventListener('focus', onReturn)
    window.addEventListener('pageshow', onReturn)
    document.addEventListener('visibilitychange', onVisibility)
    main?.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('focus', onReturn)
      window.removeEventListener('pageshow', onReturn)
      document.removeEventListener('visibilitychange', onVisibility)
      main?.removeEventListener('scroll', onScroll)
      clearScrollPulses()
      if (clearScrollLockTimerRef.current != null) {
        window.clearTimeout(clearScrollLockTimerRef.current)
      }
      revokePendingBlobs()
    }
  }, [])

  const photosForTask = (taskId: string) =>
    attachments.filter((a) => a.job_task_id === taskId)

  const handleAdd = async () => {
    if (!newLabel.trim()) return
    await addJobTask(
      jobId,
      newLabel.trim(),
      tasks.length,
      'manual',
      newPhotoRequired,
      jobLineItemUnitId
    )
    setNewLabel('')
    setNewPhotoRequired(false)
    await load()
  }

  const handleApplyTemplate = async (templateId: string) => {
    if (!templateId) return
    const tpl = templates.find((t) => t.id === templateId)
    if (!tpl?.items?.length) return
    await addJobTasksBulk(jobId, tpl.items, 'template', jobLineItemUnitId)
    await load()
  }

  const handleImportWorkSpec = async () => {
    if (!contractId) return
    const { data, error } = await supabase
      .from('contracts')
      .select('document_data')
      .eq('id', contractId)
      .single()
    if (error) {
      alert('Could not load contract work specs')
      return
    }
    const specs = (data?.document_data as any)?.workSpecs as WorkSpecRow[] | undefined
    const items = workSpecsToChecklistItems(specs)
    if (items.length === 0) {
      alert('No work specifications found on this contract.')
      return
    }
    await addJobTasksBulk(jobId, items, 'work_spec', jobLineItemUnitId)
    await load()
  }

  const handleToggleTask = async (task: JobTask, checked: boolean) => {
    if (checked && task.photo_required && !canBypassPhoto) {
      const hasPhoto = photosForTask(task.id).length > 0
      if (!hasPhoto) {
        alert('Add a photo for this item before checking it off.')
        return
      }
    }
    try {
      await toggleJobTask(task.id, checked)
      patchCachedJobTaskDone(jobId, task.id, checked)
      await load()
    } catch (e) {
      alert((e as Error).message)
    }
  }

  const handlePhotoModeMarkDone = async (task: JobTask) => {
    if (task.photo_required && !canBypassPhoto) {
      const hasPhoto = photosForTask(task.id).length > 0
      if (!hasPhoto) {
        alert('Add a photo for this item before checking it off.')
        return
      }
    }
    try {
      await toggleJobTask(task.id, true)
      const nextTasks = tasks.map((t) => (t.id === task.id ? { ...t, is_done: true } : t))
      setTasks(nextTasks)
      patchCachedJobTaskDone(jobId, task.id, true)
      const nextId = nextUnfinishedTaskId(nextTasks, task.id)
      setPhotoModeTaskId(nextId)
      if (nextId === null) writeSavedPhotoMode(photoModeStorageKeyForJob, null)
      await load({ forceDoneTaskIds: [task.id] })
    } catch (e) {
      alert((e as Error).message)
    }
  }

  const skipPhotoModeTask = () => {
    if (!photoModeTaskId) return
    setPhotoModeTaskId(nextUnfinishedTaskId(tasks, photoModeTaskId))
  }

  const handleTaskPhoto = async (
    taskId: string,
    files: FileList | null,
    captureSource: JobCaptureSource
  ) => {
    if (!files?.length) return
    // Prefer click-time snapshot; refresh if somehow missing (attach flow without prior save).
    if (!scrollSnapshotRef.current) rememberScroll(taskId)
    else scrollLockTaskIdRef.current = taskId

    const imageFiles = Array.from(files).filter((f) =>
      isChecklistImageFile(f, captureSource)
    )
    if (!imageFiles.length) return

    const taskBefore = tasks.find((t) => t.id === taskId)
    const shouldAutoCheck = !!taskBefore?.photo_required && !taskBefore.is_done
    const pendingIds = imageFiles.map(
      (_, index) => `pending-${taskId}-${Date.now()}-${index}`
    )

    // Paint thumbnail + checkmark before GPS/upload (iOS often stalls paints around camera return).
    const pending: JobAttachment[] = imageFiles.map((file, index) => {
      const blobUrl = URL.createObjectURL(file)
      pendingBlobUrlsRef.current.push(blobUrl)
      return {
        id: pendingIds[index],
        job_id: jobId,
        job_task_id: taskId,
        file_url: blobUrl,
        file_name: file.name || `photo-${index + 1}.jpg`,
        attachment_type: 'photo',
        created_at: new Date().toISOString(),
      }
    })
    setAttachments((prev) => [...pending, ...prev])

    let nextPhotoTaskId: string | null | undefined
    if (shouldAutoCheck) {
      const nextTasks = tasks.map((t) => (t.id === taskId ? { ...t, is_done: true } : t))
      setTasks(nextTasks)
      patchCachedJobTaskDone(jobId, taskId, true)
      if (photoModeRef.current) {
        nextPhotoTaskId = nextUnfinishedTaskId(nextTasks, taskId)
      }
    } else if (photoModeRef.current) {
      nextPhotoTaskId = nextUnfinishedTaskId(tasks, taskId)
    }
    pulseRememberedScroll()

    // Advance photo mode and release scroll lock immediately — don't wait on upload.
    if (photoModeRef.current && nextPhotoTaskId !== undefined) {
      setPhotoModeTaskId(nextPhotoTaskId)
      if (nextPhotoTaskId === null) writeSavedPhotoMode(photoModeStorageKeyForJob, null)
      clearScrollLock()
    } else {
      requestAnimationFrame(() => {
        applyRememberedScroll()
        pulseRememberedScroll()
        requestAnimationFrame(() => {
          applyRememberedScroll()
          scheduleClearScrollLock(1600)
        })
      })
    }

    const cam = cameraRefs.current[taskId]
    const fileInput = fileRefs.current[taskId]
    if (cam) cam.value = ''
    if (fileInput) fileInput.value = ''

    const queueForLater = async () => {
      for (let i = 0; i < imageFiles.length; i++) {
        await enqueueJobPhoto({
          id: pendingIds[i],
          jobId,
          jobTaskId: taskId,
          file: imageFiles[i],
          captureSource,
          shouldAutoCheck: shouldAutoCheck && i === 0,
        })
      }
      void syncOfflinePhotoQueue()
    }

    void (async () => {
      // Offline: queue only — no network toggle/refetch (those fail and can
      // kick auth reload → Access Denied on PWA/camera return).
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        await queueForLater()
        return
      }

      try {
        // Upload first: DB trigger blocks is_done until a linked photo exists.
        for (let i = 0; i < imageFiles.length; i++) {
          const file = imageFiles[i]
          const pendingId = pendingIds[i]
          const uploaded = await uploadJobFile(
            jobId,
            file,
            'photo',
            undefined,
            captureSource,
            taskId
          )
          setAttachments((prev) => {
            const withoutPending = prev.filter((a) => a.id !== pendingId)
            const pendingRow = prev.find((a) => a.id === pendingId)
            if (pendingRow?.file_url?.startsWith('blob:')) {
              revokePendingBlobs([pendingRow.file_url])
            }
            const withoutDup = withoutPending.filter((a) => a.id !== uploaded.id)
            return [{ ...uploaded, attachment_type: 'photo' }, ...withoutDup]
          })
        }
        if (shouldAutoCheck) {
          try {
            await toggleJobTask(taskId, true)
            patchCachedJobTaskDone(jobId, taskId, true)
          } catch {
            /* keep optimistic check; gate reconcile / sync will retry */
          }
        }
        await load({ forceDoneTaskIds: shouldAutoCheck ? [taskId] : undefined })
      } catch (e) {
        if (isLikelyOfflineError(e)) {
          await queueForLater()
        } else {
          setAttachments((prev) => {
            const doomed = prev.filter((a) => pendingIds.includes(a.id))
            revokePendingBlobs(doomed.map((a) => a.file_url).filter((u) => u.startsWith('blob:')))
            return prev.filter((a) => !pendingIds.includes(a.id))
          })
          if (taskBefore) {
            setTasks((prev) =>
              prev.map((t) => (t.id === taskId ? { ...t, is_done: taskBefore.is_done } : t))
            )
            if (shouldAutoCheck) patchCachedJobTaskDone(jobId, taskId, taskBefore.is_done)
          }
          alert('Photo upload failed: ' + (e as Error).message)
        }
      }
    })()
  }

  const done = tasks.filter((t) => t.is_done).length
  const total = tasks.length
  const unfinishedCount = total - done
  const photoModeTask = photoModeTaskId
    ? tasks.find((t) => t.id === photoModeTaskId) || null
    : null
  const photoModePhotos = photoModeTask ? photosForTask(photoModeTask.id).length : 0
  const photoModeAction =
    photoModeTask && photoModeActionForTask(photoModeTask, photoModePhotos) === 'take_photo'
      ? 'take_photo'
      : 'mark_done'
  const progress = photoModeProgress(photoModeSessionTotal, unfinishedCount)
  const canSkipPhotoMode =
    !!photoModeTask &&
    unfinishedCount > 1 &&
    nextUnfinishedTaskId(tasks, photoModeTask.id) !== photoModeTask.id

  const flowChecklistComplete = flowOnly && unfinishedCount <= 0
  const pendingAttachmentCount = attachments.filter((a) => isPendingAttachmentId(a.id)).length
  const pendingUploadCount = pendingAttachmentCount + queuedPhotoCount
  const uploadsPending = pendingUploadCount > 0

  useEffect(() => {
    if (!flowChecklistComplete || !onCompleteJob) return
    let cancelled = false
    const refresh = async () => {
      const count = await countQueuedPhotosForJob(jobId)
      if (!cancelled) setQueuedPhotoCount(count)
    }
    void refresh()
    const id = window.setInterval(refresh, 2000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [flowChecklistComplete, onCompleteJob, jobId, attachments])

  const showFullChecklist = !flowOnly

  if (loading) return <p className="text-sm text-gray-500">Loading checklist...</p>

  return (
    <div ref={rootRef} className="space-y-3">
      {showFullChecklist && (
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <p className="text-sm text-gray-600">
          {total === 0 ? 'No tasks yet.' : `${done}/${total} complete`}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {!photoMode && unfinishedCount > 0 && (
            <button
              type="button"
              onClick={() => enterPhotoMode()}
              className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {done === 0 ? tJobs('photoMode.startChecklist') : tJobs('photoMode.enter')}
            </button>
          )}
          {canEditChecklist && (
            <div className="flex flex-wrap gap-2">
              {sortedTemplates.length > 0 && (
                <select
                  defaultValue=""
                  onChange={(e) => {
                    handleApplyTemplate(e.target.value)
                    e.target.value = ''
                  }}
                  className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                >
                  <option value="">Apply template…</option>
                  {sortedTemplates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                      {t.service_type ? ` (${t.service_type})` : ''}
                    </option>
                  ))}
                </select>
              )}
              {contractId && (
                <button
                  type="button"
                  onClick={handleImportWorkSpec}
                  className="text-sm px-2 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Import work spec
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {flowOnly && photoModeTask && (
        <input
          ref={flowCameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleTaskPhoto(photoModeTask.id, e.target.files, 'camera')}
        />
      )}

      {photoMode && (
        <div
          data-testid="photo-mode-panel"
          className="sticky top-0 z-20 rounded-lg border border-blue-200 bg-white p-3 space-y-3 shadow-sm"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {tJobs('photoMode.progress', {
                  current: progress.current,
                  total: progress.total,
                })}
              </p>
              <p className="text-lg font-semibold text-gray-900 leading-snug mt-0.5">
                {progress.complete ? tJobs('photoMode.allDone') : photoModeTask?.label}
              </p>
            </div>
            {!flowOnly && (
            <button
              type="button"
              onClick={exitPhotoMode}
              className="inline-flex items-center gap-1 flex-shrink-0 text-sm text-gray-600 px-2 py-1 rounded-lg hover:bg-gray-50"
            >
              <XIcon className="w-4 h-4" />
              {tJobs('photoMode.exit')}
            </button>
            )}
          </div>
          {!progress.complete && photoModeTask && (
            <div className="flex flex-col gap-2">
              {photoModeAction === 'take_photo' ? (
                <button
                  type="button"
                  onClick={() => {
                    rememberScroll(photoModeTask.id)
                    if (flowOnly) flowCameraRef.current?.click()
                    else cameraRefs.current[photoModeTask.id]?.click()
                  }}
                  className="inline-flex items-center justify-center gap-2 w-full min-h-[48px] px-4 py-3 bg-blue-600 text-white text-base font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <CameraIcon className="w-5 h-5" />
                  {tJobs('photoMode.takePhoto')}
                </button>
              ) : flowOnly && !photoModeTask.photo_required ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      rememberScroll(photoModeTask.id)
                      flowCameraRef.current?.click()
                    }}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 min-h-[48px] px-3 py-3 border border-blue-600 text-blue-600 text-base font-semibold rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <CameraIcon className="w-5 h-5 shrink-0" />
                    {tJobs('photoMode.addPhoto')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handlePhotoModeMarkDone(photoModeTask)}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 min-h-[48px] px-3 py-3 bg-blue-600 text-white text-base font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <CheckIcon className="w-5 h-5 shrink-0" />
                    {tJobs('photoMode.markDone')}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => void handlePhotoModeMarkDone(photoModeTask)}
                  className="inline-flex items-center justify-center gap-2 w-full min-h-[48px] px-4 py-3 bg-blue-600 text-white text-base font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <CheckIcon className="w-5 h-5" />
                  {tJobs('photoMode.markDone')}
                </button>
              )}
              {canSkipPhotoMode && (
                <button
                  type="button"
                  onClick={skipPhotoModeTask}
                  className="text-sm text-gray-600 py-2 hover:text-gray-900"
                >
                  {tJobs('photoMode.skip')}
                </button>
              )}
            </div>
          )}
          {flowOnly && onCompleteJob && progress.complete && (
            <div className="flex flex-col gap-2" data-testid="flow-complete-panel">
              {uploadsPending && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {pendingAttachmentCount > 0
                    ? tJobs('photoMode.uploading')
                    : tField('pendingPhotosUpload', { count: pendingUploadCount })}
                </p>
              )}
              <button
                type="button"
                disabled={completeBusy || uploadsPending}
                onClick={() => onCompleteJob()}
                className="inline-flex items-center justify-center gap-2 w-full min-h-[48px] px-4 py-3 bg-green-600 text-white text-base font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {completeBusy ? (
                  <Loader2Icon className="w-5 h-5 animate-spin" />
                ) : (
                  <CheckCircleIcon className="w-5 h-5" />
                )}
                {tJobs('completeJob')}
              </button>
            </div>
          )}
        </div>
      )}

      {showFullChecklist && total > 0 && (
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all"
            style={{ width: `${Math.round((done / total) * 100)}%` }}
          />
        </div>
      )}

      {showFullChecklist &&
        tasks.map((t) => {
        const taskPhotos = photosForTask(t.id)
        const needsPhoto = !!t.photo_required && taskPhotos.length === 0
        const isCurrentPhotoMode = photoMode && t.id === photoModeTaskId
        return (
          <div
            key={t.id}
            ref={(el) => {
              taskRowRefs.current[t.id] = el
            }}
            className={`rounded-lg border p-2 space-y-2 ${
              isCurrentPhotoMode
                ? 'border-blue-400 bg-blue-50/50'
                : needsPhoto
                  ? 'border-amber-300 bg-amber-50/40'
                  : 'border-gray-100'
            }`}
          >
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={t.is_done}
                onChange={(e) => handleToggleTask(t, e.target.checked)}
                className="rounded border-gray-300"
                title={
                  needsPhoto
                    ? tJobs('photosRequired')
                    : undefined
                }
              />
              <span
                className={`flex-1 text-sm ${
                  t.is_done ? 'line-through text-gray-400' : 'text-gray-900'
                }`}
              >
                {t.label}
              </span>
              {showOneOffBadges && (!t.source || t.source === 'manual') && (
                <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                  This visit only
                </span>
              )}
              {canEditChecklist && (
                <label className="inline-flex items-center gap-1 text-[11px] text-gray-600 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={!!t.photo_required}
                    onChange={(e) =>
                      setJobTaskPhotoRequired(t.id, e.target.checked).then(() => load())
                    }
                    className="rounded border-gray-300"
                  />
                  Photo req.
                </label>
              )}
              {canEditChecklist && (
                <button
                  type="button"
                  onClick={() => deleteJobTask(t.id).then(() => load())}
                  className="text-red-500"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 pl-7">
              <input
                ref={(el) => {
                  cameraRefs.current[t.id] = el
                }}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleTaskPhoto(t.id, e.target.files, 'camera')}
              />
              {canEditChecklist && (
                <input
                  ref={(el) => {
                    fileRefs.current[t.id] = el
                  }}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleTaskPhoto(t.id, e.target.files, 'upload')}
                />
              )}
              <button
                type="button"
                onClick={() => {
                  rememberScroll(t.id)
                  cameraRefs.current[t.id]?.click()
                }}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <CameraIcon className="w-3 h-3" />
                Photo
              </button>
              {canEditChecklist && (
                <button
                  type="button"
                  onClick={() => {
                    rememberScroll(t.id)
                    fileRefs.current[t.id]?.click()
                  }}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  <UploadIcon className="w-3 h-3" />
                  Attach
                </button>
              )}
              {t.photo_required && (
                <span
                  className={`text-[10px] ${
                    taskPhotos.length > 0 ? 'text-green-700' : 'text-amber-700'
                  }`}
                >
                  {taskPhotos.length > 0
                    ? `${taskPhotos.length} photo(s)`
                    : tJobs('photoRequired')}
                </span>
              )}
              {!t.photo_required && taskPhotos.length > 0 && (
                <span className="text-[10px] text-gray-500">{taskPhotos.length} photo(s)</span>
              )}
            </div>

            {taskPhotos.length > 0 && (
              <div className="flex flex-wrap gap-2 pl-7">
                {taskPhotos.map((p) => (
                  <div key={p.id} className="relative group">
                    <a href={p.file_url} target="_blank" rel="noopener noreferrer">
                      <img
                        src={p.file_url}
                        alt={p.caption || p.file_name || ''}
                        className="w-14 h-14 object-cover rounded border border-gray-200"
                      />
                    </a>
                    {!isPendingAttachmentId(p.id) && (
                      <button
                        type="button"
                        title="Remove photo"
                        onClick={() => deleteJobAttachment(p.id).then(() => load())}
                        className="absolute -top-1 -right-1 bg-white border border-gray-200 rounded-full p-0.5 text-red-500 opacity-0 group-hover:opacity-100"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
      {showFullChecklist && canEditChecklist && (
        <div className="flex flex-wrap gap-2 items-center">
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Add task for this visit…"
            className="flex-1 min-w-[12rem] px-3 py-2 border border-gray-300 rounded-lg text-sm"
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
          <label className="inline-flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
            <input
              type="checkbox"
              checked={newPhotoRequired}
              onChange={(e) => setNewPhotoRequired(e.target.checked)}
              className="rounded border-gray-300"
            />
            {tJobs('photoRequired')}
          </label>
          <button
            type="button"
            onClick={handleAdd}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}
